/**
 * supernovaContracts — on-chain addresses, token ids and ABIs for the Supernova
 * staking feature. One file, so a contract detail is never duplicated inline.
 *
 * The addresses and ids below are VERIFIED facts from SUPERNOVA_HANDOFF.md.
 * Do not re-derive them, do not "fix" them, do not add addresses that are not
 * in that handoff.
 *
 * ABIs are plain human-readable signature arrays (viem's parseAbi accepts them
 * directly) so this module stays dependency-free and importable in tests
 * without pulling viem into jsdom. The one exception is the staking contract's
 * own interface, which lives as a verified JSON artifact in
 * src/config/supernovaStakingAbi.json (see SUPERNOVA_ABI_FACTS.md) and is read
 * through supernovaWriteArgs — so no signature of it is ever retyped by hand.
 */

/** Arbitrum One. */
export const SUPERNOVA_CHAIN_ID = 42161;

/** ERC-1155 collection holding the four Supernova NFTs. */
export const SUPERNOVA_COLLECTION_ADDRESS =
  "0x99ece882c219086628c01921c2cdb16c88e0b3cd";

/**
 * The LIVE staking contract — `ARCNFTStakeProtocol` on Arbitrum One. Its full
 * interface is verified in SUPERNOVA_ABI_FACTS.md: 297 stakes ever, 28 open at
 * the time of that verification. (An earlier "~80 stakers" note here was not
 * reproducible from the contract's own history and has been dropped.)
 *
 * This app only ever READS it, or asks the MEMBER'S OWN WALLET to write to it.
 */
export const SUPERNOVA_STAKING_ADDRESS =
  "0x0a1b57ea11029132B6fEDb644e5c9535DaB8Fd08";

/** Deploy block of the staking contract — the floor for any log scan. */
export const SUPERNOVA_STAKING_DEPLOY_BLOCK = 410278748;

/** The four token ids, in display order. */
export const SUPERNOVA_TOKENS = [
  { id: 7, name: "Celestial Flare" },
  { id: 8, name: "Cosmic Pulse" },
  { id: 9, name: "Galactic Ember" },
  { id: 10, name: "Stellar Nova" },
];

export const SUPERNOVA_TOKEN_IDS = SUPERNOVA_TOKENS.map((t) => t.id);

/** Standard ERC-1155 read. This one IS verified — it's the ERC-1155 standard. */
export const ERC1155_BALANCE_ABI = [
  "function balanceOfBatch(address[] accounts, uint256[] ids) view returns (uint256[])",
];

/**
 * Operator approval, also straight out of the ERC-1155 standard (EIP-1155), so
 * these signatures are verified in the same sense balanceOfBatch is.
 *
 * A member must approve the staking contract as an operator on the collection
 * ONCE before their first stake, because the stake transaction is the staking
 * contract pulling the NFT out of their wallet. `isApprovedForAll` is a view and
 * is safe to read; `setApprovalForAll` is a member-signed WRITE — sent from the
 * member's own wallet by supernovaChain.setApprovalForAll, never by this app.
 * Verified on-chain: 53 wallets have granted exactly this approval on the
 * Supernova collection, one per wallet rather than one per stake.
 */
export const ERC1155_APPROVAL_ABI = [
  "function isApprovedForAll(address account, address operator) view returns (bool)",
  "function setApprovalForAll(address operator, bool approved)",
];

/**
 * Events emitted by the staking contract. Their full parameter and indexed
 * layouts ARE now verified (SUPERNOVA_ABI_FACTS.md — all 572 historical logs
 * decode cleanly) and live in supernovaStakingAbi.json; these names stay here
 * purely as documentation. Nothing in this app decodes logs.
 */
export const SUPERNOVA_STAKING_EVENT_NAMES = [
  "TokenStaked",
  "TokenUnstaked",
  "TokenEmergencyUnstaked",
];

/**
 * Read descriptor for member stake lots — NOW WIRED, and it lives in
 * ./supernovaStakeRead as SUPERNOVA_STAKING_READ.
 *
 * SUPERNOVA_ABI_FACTS.md closed V-1/V-2/V-3/V-6: the two reads exist and are
 * verified — `getUserStakes(address) → uint256[]` and
 * `stakes(uint256) → (user, token, tokenId, amount, duration, startTime,
 * unlockTime, status)`, with `duration` in seconds. Both fragments are in
 * supernovaStakingAbi.json.
 *
 * It is not in this file only because the seconds→days conversion it needs
 * lives in supernovaWriteArgs, which already imports this module — importing it
 * back here would close a cycle.
 */
